import React from "react";
import moment from "moment-timezone";
import * as Flags from "country-flag-icons/react/3x2";
import { getCountryNamePt } from "assets/data/countries-pt";
import "./auth-screen.css";
import "./session-superseded.css";

/****************************************************
 * ##: Session superseded screen
 * Shown when the API answers 401 session_superseded: a newer login on another computer/browser took
 * the single active session (SalesPark keeps one session at a time; team management is the path for
 * several people). Uses the shared auth-screen chrome (auth-screen.css, asc-) with the security tile;
 * its own markup is the device-evidence card signature: the identity of the login that took over
 * (browser, flag + place, IP, time) so the user can judge "was this me?". "Iniciar sessão aqui" re-logs
 * in and reclaims the session.
 * @param {Object} props - { session: { device, ip, geo, at }, onReconnect }
 * History:
 * 25-10-2026: Created
 * 26-10-2026: Aligned with the login-failed screen and moved the shared chrome into auth-screen.css (asc-); this file keeps only the device-evidence card
 ****************************************************/

// Inline glyphs (currentColor), so the screen stays plain HTML like the crash screen.
const LockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.4" />
    <path d="M8 10.5V7.4a4 4 0 0 1 8 0v3.1" />
    <circle cx="12" cy="15.4" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const MonitorGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4.5" width="18" height="12" rx="2" />
    <path d="M8.5 20.5h7M12 16.5v4" />
  </svg>
);

const GlobeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.4" />
    <path d="M3.6 12h16.8M12 3.6c2.4 2.3 2.4 14.5 0 16.8M12 3.6c-2.4 2.3-2.4 14.5 0 16.8" />
  </svg>
);

const ClockGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="8.4" />
    <path d="M12 7.6V12l3 1.8" />
  </svg>
);

// Browser + OS from a user-agent string. Order matters: Chrome UAs also contain "Safari", and Edge
// contains "Chrome", so the more specific brands are tested first. Best-effort, no dependency.
const prettyUserAgent = (ua) => {
  if (!ua || typeof ua !== "string") return "Dispositivo desconhecido";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\/|Opera/.test(ua)
    ? "Opera"
    : /Chrome\//.test(ua)
    ? "Chrome"
    : /Firefox\//.test(ua)
    ? "Firefox"
    : /Safari\//.test(ua)
    ? "Safari"
    : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /iPhone|iPad|iPod|iOS/.test(ua)
    ? "iOS"
    : /Macintosh|Mac OS/.test(ua)
    ? "macOS"
    : /Android/.test(ua)
    ? "Android"
    : /Linux/.test(ua)
    ? "Linux"
    : "";
  return os ? `${browser} em ${os}` : browser;
};

// "Cidade, Região, País", dropping blanks and de-duplicating (city and region are often the same).
const prettyLocation = (geo) => {
  if (!geo || typeof geo !== "object") return null;
  const seen = new Set();
  const parts = [];
  [geo.city, geo.region_name].forEach((part) => {
    if (!part) return;
    const key = String(part).trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    parts.push(part);
  });
  const country = geo.country ? getCountryNamePt(geo.country) || geo.country : null;
  if (country) parts.push(country);
  return parts.length ? parts.join(", ") : null;
};

const prettyTime = (at) => {
  if (!at) return null;
  const date = moment(at);
  return date.isValid() ? date.format("DD-MM-YYYY HH:mm") : null;
};

const SUPPORT_URL = "https://ajuda.salespark.io";

const Auth0SessionSupersededScreen = ({ session, onReconnect }) => {
  const s = session || {};
  const geo = s.geo || {};
  const countryCode = geo.country ? String(geo.country).toUpperCase() : null;
  const Flag = countryCode && Flags[countryCode] ? Flags[countryCode] : null;
  const location = prettyLocation(geo);
  const when = prettyTime(s.at);

  return (
    <div className="asc-page">
      <div className="asc-shell">
        <div className="asc-panel">
          <img className="asc-brand" src="/img/logo_grey.svg" alt="SalesPark" />

          <div className="asc-head">
            <div className="asc-tile asc-tile--security">
              <LockGlyph />
            </div>
            <div>
              <h1 className="asc-title">A tua sessão foi iniciada noutro computador ou browser</h1>
              <p className="asc-sub">
                Por segurança, a SalesPark mantém apenas uma sessão ativa de cada vez. Para gerires a conta com mais do que uma pessoa, usa a{" "}
                <strong className="sess-strong">Gestão da Equipa</strong>.
              </p>
            </div>
          </div>

          <div className="sess-evlabel">Sessão agora ativa em:</div>
          <div className="sess-device">
            <div className="sess-device-tile">
              <MonitorGlyph />
            </div>
            <div className="sess-device-info">
              <div className="sess-device-name">{prettyUserAgent(s.device)}</div>
              {location ? (
                <div className="sess-device-loc">
                  {Flag ? (
                    <span className="sess-flag">
                      <Flag />
                    </span>
                  ) : null}
                  <span>{location}</span>
                </div>
              ) : null}
              {s.ip || when ? (
                <div className="sess-device-meta">
                  {s.ip ? (
                    <span className="sess-meta-item">
                      <GlobeGlyph />
                      {s.ip}
                    </span>
                  ) : null}
                  {when ? (
                    <span className="sess-meta-item">
                      <ClockGlyph />
                      {when}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="asc-actions">
            <button type="button" className="sp-btn" onClick={onReconnect}>
              Iniciar sessão aqui
            </button>
          </div>

          <hr className="asc-rule" />

          <div className="asc-foot">
            <span>Não reconheces este acesso?</span>
            <a href={SUPPORT_URL} target="_blank" rel="noreferrer">
              Contacta o suporte
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth0SessionSupersededScreen;
